// src/pages/AdminDevelopmentsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiSearch, FiEdit2, FiEye, FiEyeOff, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styles from './AdminDevelopmentsPage.module.css';
import {
  AdminDevelopmentsService,
  AdminDevelopmentListItem,
  DevelopmentStatus
} from '../services/adminDevelopments.service';

export default function AdminDevelopmentsPage() {
  const { t } = useTranslation();
  const statusOptions: { value: DevelopmentStatus | ''; label: string }[] = [
    { value: '', label: t('common.all') || 'Todos' },
    { value: 'PLANNING', label: t('admin.phase_planning') || 'Planeación' },
    { value: 'PRE_SALE', label: t('admin.phase_pre_sale') || 'Preventa' },
    { value: 'UNDER_CONSTRUCTION', label: t('admin.phase_under_construction') || 'En construcción' },
    { value: 'COMPLETED', label: t('admin.phase_completed') || 'Finalizado' },
    { value: 'CANCELLED', label: t('admin.phase_cancelled') || 'Cancelado' }
  ];

  const [items, setItems] = useState<AdminDevelopmentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<DevelopmentStatus | ''>('');
  const [projectState, setProjectState] = useState<'' | 'DRAFT' | 'PUBLISHED' | 'IN_EXECUTION' | 'FINISHED' | 'CANCELLED'>('');
  const [isActive, setIsActive] = useState<boolean | ''>('');
  const [isPublished, setIsPublished] = useState<boolean | ''>('');

  const [page, setPage] = useState(1);
  const pageSize = 20;

  const canPrev = page > 1;
  const canNext = items.length === pageSize;

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await AdminDevelopmentsService.list({
        page,
        pageSize,
        q: q.trim() || undefined,
        status,
        project_state: projectState,
        isActive,
        isPublished
      });

      if (!res || (res as any).ok === false) {
        setItems([]);
        setError(((res as any)?.message as string) || t('admin.projects_load_error') || 'No se pudo cargar la información.');
        return;
      }

      const data = (res as any).data;
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setItems(list);
    } catch {
      setItems([]);
      setError(t('admin.projects_load_error') || 'No se pudo cargar la información.');
    } finally {
      setLoading(false);
    }
  };

  const phaseLabel = (value: DevelopmentStatus | null | undefined) => {
    switch (value) {
      case 'PLANNING':
        return t('admin.phase_planning') || 'Planeación';
      case 'PRE_SALE':
        return t('admin.phase_pre_sale') || 'Preventa';
      case 'UNDER_CONSTRUCTION':
        return t('admin.phase_under_construction') || 'En construcción';
      case 'COMPLETED':
        return t('admin.phase_completed') || 'Finalizado';
      case 'CANCELLED':
        return t('admin.phase_cancelled') || 'Cancelado';
      default:
        return value || '-';
    }
  };

  const projectStateLabel = (value: string | null | undefined) => {
    switch (value) {
      case 'DRAFT':
        return t('admin.project_state_draft') || 'Borrador';
      case 'PUBLISHED':
        return t('admin.project_state_published') || 'Publicado';
      case 'IN_EXECUTION':
        return t('admin.project_state_in_execution') || 'En ejecución';
      case 'FINISHED':
        return t('admin.project_state_finished') || 'Finalizado';
      case 'CANCELLED':
        return t('admin.project_state_cancelled') || 'Cancelado';
      default:
        return value || '-';
    }
  };

  const docsStatusLabel = (value: string | null | undefined) => {
    switch (value) {
      case 'NONE':
        return t('admin.docs_none') || 'Sin documentación';
      case 'UPLOADED':
        return t('admin.docs_uploaded') || 'Documentación cargada';
      case 'IN_REVIEW':
        return t('admin.docs_in_review') || 'En revisión';
      case 'OBSERVED':
        return t('admin.docs_observed') || 'Observada';
      case 'APPROVED':
        return t('admin.docs_approved') || 'Aprobada';
      default:
        return value || '-';
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, projectState, isActive, isPublished]);

  const onApplySearch = () => {
    setPage(1);
    load();
  };

  const headerSubtitle = useMemo(() => {
    const total = items.length;
    if (loading) return '';
    if (error) return '';
    return total === 1 ? t('admin.projects_count_single') || '1 proyecto' : t('admin.projects_count', { count: total }) || `${total} proyectos`;
  }, [error, items.length, loading]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('admin.projects_title') || 'Proyectos'}</h1>
          <p className={styles.subtitle}>{headerSubtitle}</p>
        </div>

        <Link to="/admin/proyectos/new" className={styles.primaryButton}>
          <FiPlus /> {t('admin.create') || 'Crear'}
        </Link>
      </header>

      <section className={styles.panel}>
        <div className={styles.toolbar}>
          <div className={styles.searchField} role="search">
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={t('admin.search') || 'Buscar'}
              className={styles.searchInput}
              onKeyDown={e => {
                if (e.key === 'Enter') onApplySearch();
              }}
            />
          </div>

          <select className={styles.select} value={status} onChange={e => setStatus(e.target.value as any)}>
            {statusOptions.map(o => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            className={styles.select}
            value={projectState}
            onChange={e => setProjectState(e.target.value as any)}
          >
            <option value="">{t('admin.project_state_label') || 'Estado proyecto'}</option>
            <option value="DRAFT">{t('admin.project_state_draft') || 'Borrador'}</option>
            <option value="PUBLISHED">{t('admin.project_state_published') || 'Publicado'}</option>
            <option value="IN_EXECUTION">{t('admin.project_state_in_execution') || 'En ejecución'}</option>
            <option value="FINISHED">{t('admin.project_state_finished') || 'Finalizado'}</option>
            <option value="CANCELLED">{t('admin.project_state_cancelled') || 'Cancelado'}</option>
          </select>

          <select
            className={styles.select}
            value={isPublished === '' ? '' : isPublished ? 'true' : 'false'}
            onChange={e => {
              const v = e.target.value;
              setIsPublished(v === '' ? '' : v === 'true');
            }}
          >
            <option value="">{t('admin.publication_label') || 'Publicación'}</option>
            <option value="true">{t('admin.publication_published') || 'Publicado'}</option>
            <option value="false">{t('admin.publication_unpublished') || 'Borrador'}</option>
          </select>

          <select
            className={styles.select}
            value={isActive === '' ? '' : isActive ? 'true' : 'false'}
            onChange={e => {
              const v = e.target.value;
              setIsActive(v === '' ? '' : v === 'true');
            }}
          >
            <option value="">{t('admin.active_label') || 'Estado'}</option>
            <option value="true">{t('admin.active_true') || 'Activo'}</option>
            <option value="false">{t('admin.active_false') || 'Inactivo'}</option>
          </select>

          <button type="button" className={styles.ghostButton} onClick={onApplySearch}>
            {t('reports.filter') || 'Aplicar'}
          </button>
        </div>

        {error ? (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className={styles.skeletonList} aria-busy="true" aria-live="polite">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`${styles.rowCard} ${styles.skeleton}`} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState} role="status" aria-live="polite">
            <div className={styles.emptyTitle}>{t('projects.no_projects') || 'No hay proyectos.'}</div>
            <div className={styles.emptyText}>
              {t('admin.create_first_project') || 'Crea un proyecto para que aparezca en el catálogo.'}
            </div>
            <div className={styles.emptyCta}>
              <Link to="/admin/proyectos/new" className={styles.primaryButton}>
                <FiPlus /> {t('admin.create') || 'Crear'}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.list}>
              {items.map(it => (
                <div key={it.id} className={styles.rowCard}>
                  <div className={styles.rowMain}>
                    <div className={styles.rowTitleWrap}>
                      <div className={styles.rowTitle}>{it.name}</div>
                      <div className={styles.rowSub}>
                        {it.city}, {it.state} • {it.country}
                      </div>
                    </div>

                    <div className={styles.badges}>
                      <span className={`${styles.chip} ${it.is_active ? styles.chipActive : styles.chipInactive}`}>
                        {it.is_active ? (
                          <>
                            <FiCheckCircle /> Activo
                          </>
                        ) : (
                          <>
                            <FiXCircle /> Inactivo
                          </>
                        )}
                      </span>

                      <span className={styles.chip}>
                        {t('admin.phase_label') || 'Fase'}: {phaseLabel(it.status)}
                      </span>
                      {it.project_state ? (
                        <span className={styles.chip}>
                          {t('admin.project_state_short') || 'Estado'}: {projectStateLabel(it.project_state)}
                        </span>
                      ) : null}
                      {it.documentation_status ? (
                        <span className={styles.chip}>
                          {t('admin.docs_label') || 'Docs'}: {docsStatusLabel(it.documentation_status)}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className={styles.rowActions}>
                    <Link to={`/admin/proyectos/${it.id}/edit`} className={styles.actionButton}>
                      <FiEdit2 /> {t('admin.edit') || 'Editar'}
                    </Link>
                    <Link to={`/admin/proyectos/${it.id}/documentos`} className={styles.actionButton}>
                      {t('common.documents') || 'Documentos'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                disabled={!canPrev}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                {t('common.prev') || 'Anterior'}
              </button>
              <div className={styles.pageInfo}>
                {t('common.page') || 'Página'} {page}
              </div>
              <button
                type="button"
                className={styles.pageButton}
                disabled={!canNext}
                onClick={() => setPage(p => p + 1)}
              >
                {t('common.next') || 'Siguiente'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
